import type { ScorecardBrand } from "@models/Scorecard";

/** Primary first, then Competitors in source-question order — the default
 *  sequence new/reset Scorecards start from (mirrors reorderBrands in
 *  scorecard-config-slice, which governs `brands` membership/role order,
 *  not display order). */
export function getDefaultBrandOrder(brands: ScorecardBrand[]): ScorecardBrand[] {
  const primary = brands.find((b) => b.role === "primary");
  const competitors = brands
    .filter((b) => b.role === "competitor")
    .sort((a, b) => a.sourceOrder - b.sourceOrder);
  return primary ? [primary, ...competitors] : competitors;
}

/**
 * All brands in their configured display order: entries from columnOrder
 * first (in that order), then any brand not yet present in columnOrder —
 * newly added, or from a config saved before this field existed — appended
 * in default order (Primary first, then source order). Self-healing:
 * callers that persist a new order can always store this function's own
 * output back.
 */
export function getOrderedBrands(
  brands: ScorecardBrand[],
  columnOrder: string[],
): ScorecardBrand[] {
  const byId = new Map(brands.map((b) => [b.id, b]));
  const ordered = columnOrder
    .map((id) => byId.get(id))
    .filter((b): b is ScorecardBrand => !!b);
  const orderedIds = new Set(ordered.map((b) => b.id));
  const remaining = getDefaultBrandOrder(brands).filter((b) => !orderedIds.has(b.id));
  return [...ordered, ...remaining];
}

/** Brand columns to render on the Scorecard table: every visible brand
 *  (Primary included) in its configured order. */
export function getVisibleOrderedBrands(
  brands: ScorecardBrand[],
  columnOrder: string[],
  hiddenBrandIds: string[],
): ScorecardBrand[] {
  return getOrderedBrands(brands, columnOrder).filter((b) => !hiddenBrandIds.includes(b.id));
}
